import React, { useEffect, useRef, useState } from "react";
import { loadModules } from "esri-loader";
import TopHeader from "../components/layout/TopHeader";
import SidePanel from "../components/layout/SidePanel";
import MapViewComponent from "../components/map/MapView";
import Modal from "../components/layout/Modal";
import { useAuth } from "../context/AuthContext";
import { useExpansion } from "../context/ExpansionContext"; 
import { calculateBestRoute } from "../services/simulationService";
import CollapsibleCard from "../components/common/CollapsibleCard";
import RouteRow from "../components/common/RouteRow";
import RouteEditor from "../components/expansion/RouteEditor";
import { parseRouteExcel } from "../utils/excelParser";
import { validateCountry, getCityCoordinates } from "../services/geoService"; 
import { getAllCities } from "../services/routeService";
import { getRoutesByCity, getStopsByRoute } from "../services/mapService";
import * as XLSX from "xlsx-js-style";

// 🔥 GENERADOR DE COLORES PARA RUTAS CANDIDATAS 🗺️🎨
const routeColorMap = {};
let routeColorIndex = 0;
const vibrantRouteColors = [
  "#3B82F6", // Azul
  "#8B5CF6", // Morado
  "#10B981", // Verde esmeralda
  "#F97316", // Naranja brillante
  "#06B6D4", // Cyan
  "#EC4899", // Rosa
  "#84CC16", // Verde lima
  "#6366F1", // Índigo
  "#14B8A6", // Teal / Turquesa
];

function getRouteColor(routeId) {
  if (routeColorMap[routeId]) {
    return routeColorMap[routeId];
  }
  const color = vibrantRouteColors[routeColorIndex % vibrantRouteColors.length];
  routeColorIndex++;
  routeColorMap[routeId] = color;
  return color;
}

export default function ExpansionPage() {
  const { token } = useAuth();
  const mapRef = useRef(null);
  const graphicsLayerRef = useRef(null); 
  const winnerLayerRef = useRef(null);
  const viewRef = useRef(null);
    const {
        selectedCity, setSelectedCity,
        candidateRoutes, setCandidateRoutes,
        draftRoute, setDraftRoute,
        visibleCandidateIds, setVisibleCandidateIds
    } = useExpansion();
  
  // --- DATA STATE ---
const [cities, setCities] = useState([]); 
const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);

// --- CARGAR CIUDADES (Sin coordenadas, solo nombres e IDs) ---
  useEffect(() => {
    if (token) {
      const fetchCities = async () => {
        try {
          const data = await getAllCities(token);
          
          // Mapeo simple: Del Backend al Frontend
          const formattedCities = data.map(c => ({
            id: c.citId,
            name: c.citName,
            // Inicialmente NO tenemos coordenadas, se buscarán al seleccionar
            lat: null, 
            lon: null,
            originalData: c 
          }));
            console.log("✅ Ciudades cargadas:", formattedCities);

          setCities(formattedCities);
        } catch (error) {
          showModal("Error", "No se pudieron cargar las ciudades", "error");
        }
      };
      fetchCities();
    }
  }, [token]);
  

  
  // --- RUTAS STATE ---
  const [existingRoutes, setExistingRoutes] = useState([]); 
  const [visibleRouteIds, setVisibleRouteIds] = useState(new Set()); 



  // --- EDITOR STATE ---
  const [isCreating, setIsCreating] = useState(false);
  // NOTA: draftRoute ya no guarda el nombre "en vivo", solo los puntos para el mapa

  // --- SIMULATION & UI STATE ---
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [winningRouteId, setWinningRouteId] = useState(null);

  // --- POPUP STATE ---
  const [customPopup, setCustomPopup] = useState(null); 
  const customPopupRef = useRef(null);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [editValues, setEditValues] = useState({ name: "", weight: "" }); 

  const [modal, setModal] = useState({ isOpen: false, title: "", message: "", type: "info" });
  const showModal = (title, message, type) => setModal({ isOpen: true, title, message, type });
  const closeModal = () => setModal({ ...modal, isOpen: false });

  const handleSetCustomPopup = (val) => {
      customPopupRef.current = val;
      setCustomPopup(val);
  };
  // Helper
  const getRouteHash = (stops) => {
    if (!stops || stops.length === 0) return "";
    return stops.map(s => `${s.lat.toFixed(4)},${s.lon.toFixed(4)}`).join('|');
  };

  // --- DIBUJO ---
  const removeGraphicsByTag = (tag) => { 
      const layer = graphicsLayerRef.current; 
      if(layer) layer.removeMany(layer.graphics.filter(g => g.attributes?.tag === tag || g.attributes?.tag === "winner-label")); 
  };

  const drawDraftPreview = (stops, color) => { 
      if(graphicsLayerRef.current) { 
          removeGraphicsByTag("draft"); 
          drawRouteOnMap(stops, color, "draft", true, "Editando...", "candidate", "draft"); 
      } 
  };

// 🔥 NUEVO: Función para descargar la plantilla de Excel CON ESTILOS, BORDES Y COLORES
  const downloadExcelTemplate = () => {
    // 1. Datos del Excel
    const wsData = [
      ["Nombre Ruta", "Nombre Parada", "Peso", "Latitud", "Longitud"],
      ["Ruta Porlamar - Pampatar", "Terminal de Porlamar", 1000, 10.953, -63.851],
      ["", "C.C. Sambil Margarita", 50, 10.985, -63.815],
      ["", "Castillo San Carlos", 50, 10.996, -63.798],
      ["Ruta Sambil - La Asunción", "C.C. Sambil Margarita", 50, 10.985, -63.815],
      ["", "Plaza Bolívar La Asunción", 40, 11.032, -63.862]
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // 2. Definición de estilos
    // Borde negro delgado para los 4 lados
    const borderStyle = {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
    };

    // Estilo para el encabezado (Fondo verde esmeralda oscuro, letra blanca y en negrita)
    const headerStyle = {
        fill: { fgColor: { rgb: "047857" } }, // Verde #047857
        font: { bold: true, color: { rgb: "FFFFFF" } },
        border: borderStyle,
        alignment: { horizontal: "center", vertical: "center" }
    };

    // Estilo para el resto de celdas (Bordes y centrado vertical)
    const cellStyle = {
        border: borderStyle,
        alignment: { vertical: "center" }
    };

    // 3. Recorremos todas las celdas para aplicarles el estilo correspondiente
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellAddress = { c: C, r: R };
            const cellRef = XLSX.utils.encode_cell(cellAddress);

            // Si la celda está vacía (por la combinación), la creamos para que igual se le dibuje el borde
            if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };

            if (R === 0) {
                // Si es la fila 0 (Encabezados), aplicamos el headerStyle
                ws[cellRef].s = headerStyle;
            } else {
                // Si son los datos, aplicamos el cellStyle normal
                ws[cellRef].s = cellStyle;
            }
        }
    }

    // 4. Combinamos las celdas (Merges)
    ws['!merges'] = [
      { s: { r: 1, c: 0 }, e: { r: 3, c: 0 } }, // Ruta Porlamar - Pampatar
      { s: { r: 4, c: 0 }, e: { r: 5, c: 0 } }  // Ruta Sambil - La Asunción
    ];

    // 5. Ajustamos el ancho de las columnas
    ws['!cols'] = [{ wch: 25 }, { wch: 25 }, { wch: 10 }, { wch: 15 }, { wch: 15 }];

    // 6. Generamos el archivo
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla Rutas");
    XLSX.writeFile(wb, "Plantilla_Ejemplo_Margarita.xlsx");
  };

  const drawRouteOnMap = (stops, color, tag, isDraft, routeName = "", routeType = "candidate", currentRouteId = "draft") => {
    const layer = graphicsLayerRef.current;
    if (!layer || !stops || stops.length === 0) return;
    
    const allRoutes = [
        ...existingRoutes.map(r => ({...r, type: 'existing'})),
        ...candidateRoutes.map(r => ({...r, type: 'candidate'}))
    ];
    const otherRoutes = allRoutes.filter(r => r.id !== currentRouteId);
    const otherStops = otherRoutes.flatMap(r => r.stops || []);

    loadModules(["esri/Graphic"]).then(([Graphic]) => {
        if (stops.length > 1) {
            const path = stops.map(s => [isDraft ? s.lon : s.stoLongitude || s.lon, isDraft ? s.lat : s.stoLatitude || s.lat]);
            const line = new Graphic({ 
                geometry: { type: "polyline", paths: [path] }, 
                // 🔥 Usa el color de la ruta (ya sea el del borrador recién creado o el guardado)
                symbol: { type: "simple-line", color: (isDraft || routeType === 'candidate') ? color : "#6B7280", width: 3, style: (isDraft || routeType === 'candidate') ? "short-dash" : "solid" }, 
                attributes: { tag, type: "line", routeName, routeType, routeColor: color } 
            });
            layer.add(line);
        }
        stops.forEach(s => {
            const lat = isDraft ? s.lat : s.stoLatitude || s.lat; 
            const lon = isDraft ? s.lon : s.stoLongitude || s.lon;
            const isShared = otherStops.some(other => Math.abs((other.lat || other.stoLatitude) - lat) < 0.0001 && Math.abs((other.lon || other.stoLongitude) - lon) < 0.0001);
            const belongsToExisting = existingRoutes.some(r => r.stops.some(ex => Math.abs((ex.lat || ex.stoLatitude) - lat) < 0.0001 && Math.abs((ex.lon || ex.stoLongitude) - lon) < 0.0001));
            let outlineColor = isShared ? "black" : (isDraft || routeType === 'candidate' ? color : "#666");
            const strokeWidth = isShared ? 3 : 2;

            const symbol = { type: "simple-marker", style: "circle", color: "white", size: "12px", outline: { color: outlineColor, width: strokeWidth } };
            const pt = new Graphic({ geometry: { type: "point", latitude: lat, longitude: lon }, symbol, attributes: { tag, type: "stop", population: s.population || 0, isShared, routeName, routeType, stopName: s.name || s.stoName, stopId: s.id, routeColor: color,isFromExisting: belongsToExisting } });
            layer.add(pt);
        });
    });
  };

  const refreshMap = (currentExisting, currentCandidates) => {
      const layer = graphicsLayerRef.current;
      if (!layer) return;
      layer.removeAll();
      currentExisting.forEach(r => { if (visibleRouteIds.has(r.id)) drawRouteOnMap(r.stops, r.color, `existing-${r.id}`, false, r.name, "existing", r.id); });
      currentCandidates.forEach(r => { if (visibleCandidateIds.has(r.id)) drawRouteOnMap(r.stops, r.color, `candidate-${r.id}`, false, r.name, "candidate", r.id); });
  };

  // --- LÓGICA DE EDICIÓN ---
  // Esta función ahora se llama desde el Editor o desde el click del mapa
  const addDraftPoint = (lat, lon, pop = 0, isShared = false, inheritedName = "") => {
    // Validación contra sí misma
    const alreadyExists = draftRoute.stops.some(s => Math.abs(s.lat - lat) < 0.0001 && Math.abs(s.lon - lon) < 0.0001);
    if (alreadyExists) { showModal("Aviso", "Esta parada ya forma parte de la ruta.", "warning"); return; }

    setDraftRoute(prev => {
      const idx = prev.stops.length + 1;
      const defaultName = inheritedName || `Parada ${idx}`; 
      const safePop = pop === "" ? 0 : parseInt(pop); 
      const newStop = { lat, lon, population: safePop, name: defaultName, id: Date.now(), isShared: isShared };
      const updatedStops = [...prev.stops, newStop];
      
      drawDraftPreview(updatedStops, prev.color);
      return { ...prev, stops: updatedStops };
    });
  };

  // Actualización de paradas (para borrar o cambiar peso)
  const handleUpdateStops = (newStops) => {
      setDraftRoute(prev => {
          drawDraftPreview(newStops, prev.color);
          return { ...prev, stops: newStops };
      });
  };

  const startCreating = () => {
    if(!selectedCity) return showModal("Atención", "Selecciona una ciudad.", "warning");
    setIsCreating(true);
    // 🔥 ASIGNAMOS ID Y COLOR DESDE EL INICIO
    const newRouteId = Date.now();
    setDraftRoute({ id: newRouteId, name: "", color: getRouteColor(newRouteId), stops: [] });
  };

  const cancelCreating = () => {
    removeGraphicsByTag("draft");
    setDraftRoute({ name: "", color: "#EAC024", stops: [] });
    setIsCreating(false);
    handleSetCustomPopup(null);
  };

  const tryAddPoint = async (lat, lon, pop = 0, knownShared = false) => {
    if (!window.currentCity) return showModal("Error", "Selecciona ciudad.", "warning");
    // --- 2. VALIDACIÓN GEOGRÁFICA ---
    // Llamamos al servicio y esperamos el nombre del país
    const check = await validateCountry(lat, lon);

    if (!check.isValid) {
        // Mostramos el nombre exacto del país donde cayó el punto
        return showModal(
            "Ubicación no permitida", 
            `Coordenadas irreales (fuera del mapa)`, 
            "error"
        );
    }
    // --------------------------------
    let finalPop = pop;
    let isShared = knownShared;
    let finalName = "";
    
    if (!isShared) {
        const existingStops = existingRoutes.flatMap(r => r.stops || []);
        let match = existingStops.find(s => Math.abs((s.lat || s.stoLatitude) - lat) < 0.0001 && Math.abs((s.lon || s.stoLongitude) - lon) < 0.0001);
        if(!match) {
             const candStops = candidateRoutes.flatMap(r => r.stops || []);
             match = candStops.find(s => Math.abs(s.lat - lat) < 0.0001 && Math.abs(s.lon - lon) < 0.0001);
        }
        if (match) { isShared = true; finalPop = match.population || 0; finalName = match.name || match.stoName || ""; }
    }
    addDraftPoint(lat, lon, finalPop, isShared, finalName);
  };

  // 🔥 GUARDADO (Recibe el nombre final desde el hijo)
  const saveCandidateRoute = (finalName) => {
    if (!finalName.trim()) return showModal("Falta Nombre", "Ponle nombre a la ruta.", "warning");
    if (draftRoute.stops.length < 2) return showModal("Ruta Incompleta", "Mínimo 2 puntos.", "warning");
    const nameConflict = existingRoutes.some(r => r.name.toLowerCase() === finalName.trim().toLowerCase()) ||
                            candidateRoutes.some(r => r.name.toLowerCase() === finalName.trim().toLowerCase());

        if (nameConflict) {
            return showModal("Nombre Duplicado", `Ya existe una ruta llamada "${finalName.trim()}". Por favor, elige otro nombre.`, "error");
        }
    const draftHash = getRouteHash(draftRoute.stops);
    const geometryConflict = candidateRoutes.some(r => getRouteHash(r.stops) === draftHash);

    if (geometryConflict) {
        return showModal("Ruta Duplicada", "Ya existe una ruta con exactamente este recorrido.", "error");
    }

    // 🔥 Usamos los datos actuales de draftRoute que YA tienen el id y color asignados
    const newCandidate = { ...draftRoute, name: finalName }; 
    setCandidateRoutes(prev => [...prev, newCandidate]);
    setVisibleCandidateIds(prev => new Set(prev).add(newCandidate.id));
    
    drawRouteOnMap(newCandidate.stops, newCandidate.color, `candidate-${newCandidate.id}`, false, newCandidate.name, "candidate", newCandidate.id);
    removeGraphicsByTag("draft");
    setDraftRoute({ name: "", color: "#EAC024", stops: [] });
    setIsCreating(false);
    showModal("¡Guardado!", "Ruta creada.", "success");
  };

  const deleteCandidateRoute = (id) => {
      if (id === winningRouteId) {
          if (winnerLayerRef.current) winnerLayerRef.current.removeAll(); 
          setWinningRouteId(null); setSimulationResult(null);
      }
      setCandidateRoutes(prev => prev.filter(r => r.id !== id));
      removeGraphicsByTag(`candidate-${id}`);
  };

const handleExcelImport = async (e) => {
    // 1. Guardamos la referencia del input de forma síncrona
    const fileInput = e.target;
    const file = fileInput.files[0];
    if (!file) return;

    try {
        const { newRoutes, bounds } = await parseRouteExcel(file);
        for (const route of newRoutes) {
            if (route.stops.length < 2) {
                // Al hacer "throw new Error", el código salta automáticamente 
                // al "catch" de abajo y abre tu modal rojo con este mensaje.
                throw new Error(`Ruta Incompleta: La ruta "${route.name}" solo tiene ${route.stops.length} parada(s) válida(s). Cada ruta en el Excel debe contener al menos 2 puntos distintos para trazar un recorrido.`);
            }
        }
        const currentHashes = new Set(candidateRoutes.map(r => getRouteHash(r.stops)));
        
        const existingNames = new Set([
            ...existingRoutes.map(r => r.name?.toLowerCase() || ""),
            ...candidateRoutes.map(r => r.name?.toLowerCase() || "")
        ]);

        const validNewRoutes = [];

        for (const route of newRoutes) {
            // Validar nombre repetido
            if (existingNames.has(route.name.toLowerCase())) {
                throw new Error(`El Excel contiene una ruta con nombre repetido ("${route.name}"). Modifique el archivo e intente de nuevo.`);
            }
            
            // Validar recorrido exacto
            const hash = getRouteHash(route.stops);
            if (currentHashes.has(hash)) {
                throw new Error(`El recorrido de la ruta "${route.name}" es idéntico a una ruta que ya existe. Modifique el archivo e intente de nuevo.`);
            }
            
            currentHashes.add(hash);
            existingNames.add(route.name.toLowerCase());
            route.color = getRouteColor(route.id);
            validNewRoutes.push(route);
        }

        setCandidateRoutes((prev) => [...prev, ...validNewRoutes]);
        validNewRoutes.forEach(r => setVisibleCandidateIds(prev => new Set(prev).add(r.id)));

        if (viewRef.current && bounds.hasValidCoords) {
            viewRef.current.goTo({ target: [{ latitude: bounds.minLat, longitude: bounds.minLon }, { latitude: bounds.maxLat, longitude: bounds.maxLon }] }, { duration: 1000 });
        }
        
        showModal("Éxito", `Importadas ${validNewRoutes.length} rutas correctamente.`, "success");

    } catch (error) {
        console.error("Error validando Excel:", error);
        // 2. Disparamos el Modal extrayendo el mensaje de forma segura
        const errorMsg = error instanceof Error ? error.message : "Error desconocido al procesar el archivo.";
        showModal("Error de Importación", errorMsg, "error");
    } finally { 
        // 3. Limpiamos usando la referencia guardada, no e.target
        if (fileInput) fileInput.value = ""; 
    }
  };

  // --- SELECCIÓN Y TOGGLES ---
  
const selectCity = async (city) => { // 👈 Ahora es async
    // 1. Mostrar estado de "Cargando..." si quieres, o simplemente esperar
    let targetLat = city.lat;
    let targetLon = city.lon;

    // 2. Si no tiene coordenadas (que es siempre la primera vez), las buscamos en OSM
    if (!targetLat || !targetLon) {
        // Opcional: showModal("Cargando...", "Buscando ubicación...", "info");
        const coords = await getCityCoordinates(city.name);
        
        if (coords) {
            targetLat = coords.lat;
            targetLon = coords.lon;
            
            // (Opcional) Actualizamos el estado para no volver a buscarlo si el usuario vuelve a elegirla
            setCities(prev => prev.map(c => c.id === city.id ? { ...c, lat: coords.lat, lon: coords.lon } : c));
        } else {
            // Fallback: Coordenada centro de Venezuela si OSM falla
            targetLat = 7.12539; 
            targetLon = -66.16666;
            showModal("Aviso", `No se encontró ubicación exacta para ${city.name}, usando centro del país.`, "warning");
        }
            setCandidateRoutes([]); 
    setVisibleCandidateIds(new Set());
    }

    // 3. Lógica normal de selección
    // Crear un objeto ciudad actualizado con las coordenadas para el contexto
    const cityWithCoords = { ...city, lat: targetLat, lon: targetLon };
    
    setSelectedCity(cityWithCoords);
    setIsCityDropdownOpen(false);
    
    // Limpieza
    setSimulationResult(null);
    if (graphicsLayerRef.current) graphicsLayerRef.current.removeAll();
    if (winnerLayerRef.current) winnerLayerRef.current.removeAll(); 
    setVisibleRouteIds(new Set()); 

    setWinningRouteId(null);

    // 4. Mover mapa
    if(viewRef.current && targetLat && targetLon) {
        viewRef.current.goTo({ center: [targetLon, targetLat], zoom: 13 });
    }
if (token && city.id) {
        try {
            // A. Traer las rutas de la ciudad (Solo info básica: ID, Nombre, Color)
            const routesData = await getRoutesByCity(token, city.id);
            
            // B. Traer las paradas completas para cada ruta (en paralelo para rapidez)
            const routesWithStops = await Promise.all(
                routesData.map(async (route) => {
                    const stopsData = await getStopsByRoute(token, route.rouId);
                    
                    return {
                        id: route.rouId,       // Backend: rouId
                        name: route.rouName,   // Backend: rouName
                        color: route.rouColor || "#6B7280", // Backend: rouColor
                        stops: stopsData.map(s => ({
                            // Mapeo del objeto Stop del backend al formato del mapa
                            id: s.stoId || s.id,
                            name: s.stoName || s.name,
                            lat: s.stoLatitude || s.lat,
                            lon: s.stoLongitude || s.lon,
                            population: s.population || 0, // 👈 El dato clave para el Greedy
                            isShared: false // Se calculará visualmente al dibujar
                        }))
                    };
                })
            );

            console.log("✅ Rutas Dominadas Cargadas:", routesWithStops);

            // C. Guardar en estado y hacerlas visibles
            setExistingRoutes(routesWithStops);
            
            const allIds = new Set(routesWithStops.map(r => r.id));
            setVisibleRouteIds(allIds);

            // Nota: El useEffect que tienes escuchando [existingRoutes] se encargará 
            // de llamar a refreshMap() y dibujarlas automáticamente.

        } catch (error) {
            console.error("Error cargando rutas dominadas:", error);
            showModal("Error", "No se pudieron cargar las rutas de la ciudad.", "error");
        }
    }
  };

  const toggleAllExisting = () => { const allIds = existingRoutes.map(r => r.id); const isAll = allIds.every(id => visibleRouteIds.has(id)); setVisibleRouteIds(new Set(isAll ? [] : allIds)); };
  const toggleAllCandidates = () => { const allIds = candidateRoutes.map(r => r.id); const isAll = allIds.every(id => visibleCandidateIds.has(id)); setVisibleCandidateIds(new Set(isAll ? [] : allIds)); };
  const toggleExistingRoute = (r) => { setVisibleRouteIds(prev => { const n = new Set(prev); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; }); };
  const toggleCandidateRoute = (r) => { setVisibleCandidateIds(prev => { const n = new Set(prev); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; }); };

  const updateGlobalStopData = (lat, lon, newName, newWeight) => {
      const weightInt = newWeight === "" ? 0 : parseInt(newWeight);
      if (weightInt < 0) {
          return showModal("Error", "El peso de la parada no puede ser negativo.", "error");
      }
      const newCandidates = candidateRoutes.map(route => ({
          ...route, stops: route.stops.map(s => (Math.abs(s.lat - lat) < 0.0001 && Math.abs(s.lon - lon) < 0.0001) ? { ...s, name: newName, population: weightInt } : s)
      }));
      setCandidateRoutes(newCandidates);
      refreshMap(existingRoutes, newCandidates);
      handleSetCustomPopup(null)
  };

const handleRunSimulation = async () => {
    // 1. Validaciones previas
    if (!candidateRoutes || candidateRoutes.length === 0) {
        return showModal("Sin Candidatas", "Crea al menos una ruta candidata.", "warning");
    }
    if (!selectedCity || !selectedCity.id) {
        return showModal("Error", "Selecciona una ciudad válida.", "error");
    }

    // 2. Preparar UI
    setIsSimulating(true);
    setSimulationResult(null);
    
    // Limpiar capa de ganadores previa
    if (winnerLayerRef.current) winnerLayerRef.current.removeAll();

    try {
        // 3. Construir Payload para el Backend
        // Mapeamos las rutas locales al formato DTO exacto que espera Java
        const simulationPayload = {
            citId: selectedCity.id,
            candidates: candidateRoutes.map(route => ({
                name: route.name,
                color: route.color,
                stops: route.stops.map(stop => ({
                    lat: stop.lat,
                    lon: stop.lon,
                    population: stop.population || 0 // Asegurar valor numérico
                }))
            }))
        };

        // 4. Llamada al WebSocket Controller (Endpoint REST)
        // 'token' debe estar disponible en el contexto de tu componente (useAuth)
        const result = await calculateBestRoute(simulationPayload, token);

        // 5. Procesar Respuesta Exitosa (AHORA ES UN ARREGLO)
        if (result && result.length > 0) {
            const bestResult = result[0]; // El ganador absoluto siempre es el índice 0
            
            // 🔥 GUARDAMOS LA LISTA COMPLETA EN LOCALSTORAGE PARA LOS REPORTES
            localStorage.setItem("last_simulation_ranking", JSON.stringify(result));

            // Buscamos la ruta candidata local que coincida con la ganadora del backend
            const winnerLocal = candidateRoutes.find(r => r.name === bestResult.winnerRouteName);
            
            if (winnerLocal) {
                setWinningRouteId(winnerLocal.id);

                // --- LÓGICA DE DIBUJO ---
                if (graphicsLayerRef.current && winnerLayerRef.current) {
                    loadModules(["esri/Graphic", "esri/geometry/Point"]).then(([Graphic, Point]) => {
                        // Dibujar Línea
                        const path = winnerLocal.stops.map(s => [s.lon, s.lat]);
                        const line = new Graphic({ 
                            geometry: { type: "polyline", paths: [path] }, 
                            symbol: { type: "simple-line", color: "#EF4444", width: 5, style: "solid" } 
                        });
                        winnerLayerRef.current.add(line);
                        
                        // Dibujar Puntos de parada
                        winnerLocal.stops.forEach(s => {
                            const strokeWidth = s.isShared ? 3 : 2;
                            const pt = new Graphic({ 
                                geometry: { type: "point", latitude: s.lat, longitude: s.lon }, 
                                symbol: { type: "simple-marker", style: "circle", color: "white", size: "12px", outline: { color: "#EF4444", width: strokeWidth } } 
                            });
                            winnerLayerRef.current.add(pt);
                        });

                        // Dibujar Etiqueta "RUTA GANADORA"
                        if (winnerLocal.stops.length > 0) {
                            const startPoint = winnerLocal.stops[0];
                            const labelGraphic = new Graphic({
                                geometry: { type: "point", latitude: startPoint.lat, longitude: startPoint.lon },
                                symbol: {
                                    type: "text", color: "#EF4444", haloColor: "white", haloSize: "2px",
                                    text: "★ RUTA GANADORA", xoffset: 0, yoffset: 18,
                                    font: { size: 10, weight: "bold", family: "sans-serif" }
                                }
                            });
                            winnerLayerRef.current.add(labelGraphic);
                        }
                    });
                }
            }

            // Actualizar Panel de Resultados de la UI con los datos del Algoritmo
            setSimulationResult({
                winningRouteName: bestResult.winnerRouteName,
                peopleGained: bestResult.addedPopulation,
                currentCoveragePercent: bestResult.currentCityCoverage, 
                newCoveragePercent: bestResult.newTotalCoverage, // <-- Ajustado al nombre de tu DTO
                recommendationMessage: bestResult.algorithmMessage
            });
        } else {
            showModal("Aviso", "El algoritmo no encontró mejoras con las rutas propuestas.", "info");
        }

    } catch (error) {
        console.error("Error simulando expansión:", error);
        showModal("Error de Simulación", "No se pudo calcular la mejor ruta. Revisa la consola.", "error");
    } finally {
        setIsSimulating(false);
    }
  };

  // --- EFECTOS ---
  useEffect(() => {
      if (selectedCity) localStorage.setItem("expansion_city", JSON.stringify(selectedCity));
      localStorage.setItem("expansion_candidates", JSON.stringify(candidateRoutes));
  }, [selectedCity, candidateRoutes]);

  useEffect(() => {
      if (graphicsLayerRef.current) refreshMap(existingRoutes, candidateRoutes);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingRoutes, candidateRoutes, visibleRouteIds, visibleCandidateIds]);

  useEffect(() => {
    if (winnerLayerRef.current) {
        winnerLayerRef.current.visible = !(winningRouteId && !visibleCandidateIds.has(winningRouteId));
    }
  }, [visibleCandidateIds, winningRouteId]);

  // MAPA INIT
// MAPA INIT
  useEffect(() => {
    let mounted = true;
    const initMap = async () => {
      const [Map, MapView, GraphicsLayer, Zoom] = await loadModules([
        "esri/Map", "esri/views/MapView", "esri/layers/GraphicsLayer", "esri/widgets/Zoom"
      ]);
      if (!mounted) return; // el componente se desmontó mientras cargaba ArcGIS

      const map = new Map({ basemap: "streets-navigation-vector" }); 
      const gLayer = new GraphicsLayer();
      const wLayer = new GraphicsLayer();
      map.add(gLayer);
      map.add(wLayer); 
      graphicsLayerRef.current = gLayer;
      winnerLayerRef.current = wLayer;

      const defaultCenter = selectedCity ? [selectedCity.lon, selectedCity.lat] : [-66.850, 10.480];
      
      const mapView = new MapView({
        container: mapRef.current, map: map, center: defaultCenter, zoom: 13,
        ui: { components: [] }, popup: { autoOpenEnabled: false, dockEnabled: false, visible: false }
      });

      mapView.when(() => {
        const zoom = new Zoom({ view: mapView });
        mapView.ui.add(zoom, "top-left");
        const style = document.createElement("style");
        style.innerHTML = `.esri-view-surface--inset-outline:focus::after { display: none !important; } .esri-view:focus { outline: none !important; }`;
        document.head.appendChild(style);
        
        mapView.container.addEventListener("focus", (e) => e.target.blur(), true);

        if (candidateRoutes.length > 0) {
            candidateRoutes.forEach(route => {
                drawRouteOnMap(route.stops, route.color, `candidate-${route.id}`, false, route.name, "candidate", route.id);
                setVisibleCandidateIds(prev => new Set(prev).add(route.id));
            });
        }
        if (selectedCity) selectCity(selectedCity, false);
      });

      viewRef.current = mapView;

      // 1. EVENTO HOVER: VISTA PREVIA RÁPIDA
      mapView.on("pointer-move", async (event) => {
        if (window.isCreatingMode) return; 
        if (customPopupRef.current?.isFixed) return; // Si ya hay uno fijo (editando o viendo), no hacer hover

        const response = await mapView.hitTest(event);
        const graphicHit = response.results.find(res => 
            res.graphic?.attributes && 
            (res.graphic.attributes.type === "stop" || res.graphic.attributes.type === "line")
        );

        if (graphicHit) {
             const attr = graphicHit.graphic.attributes;
             const screenPoint = mapView.toScreen(graphicHit.mapPoint || event.mapPoint);
             
             if (attr.type === "stop") {
                 const geometry = graphicHit.graphic.geometry;
                 handleSetCustomPopup({
                     x: screenPoint.x, y: screenPoint.y - 40,
                     title: attr.isShared ? "Parada Compartida" : "Parada",
                     subtitle: attr.stopName || "Sin Nombre",
                     extra: `Peso: ${attr.population || 0}`, isStop: true, isEditing: false,
                     lat: geometry.latitude, lon: geometry.longitude,
                     currentName: attr.stopName, currentWeight: attr.population, stopId: attr.stopId,
                     routeColor: attr.routeColor,
                     isFromExisting: attr.isFromExisting,
                     isFixed: false // Hover: no se fija
                 });
             } else if (attr.type === "line") {
                 let title = attr.isWinner ? "🏆 RUTA GANADORA" : (attr.routeType === "existing" ? "Ruta Dominada" : "Ruta Candidata");
                 handleSetCustomPopup({ x: screenPoint.x, y: screenPoint.y - 20, title, subtitle: attr.routeName || "Sin nombre", isStop: false, routeColor: attr.routeColor, isFixed: false });
             }
             document.body.style.cursor = "pointer";
        } else {
             handleSetCustomPopup(null);
             document.body.style.cursor = "default";
        }
      });

      // 2. EVENTO CLIC: CREAR PUNTOS O ABRIR EDICIÓN DIRECTA
      mapView.on("immediate-click", async (event) => {
        event.stopPropagation();
        
        // Si el botón "+ Crear Nueva Ruta" está activado, el clic pone puntos
        if (window.isCreatingMode) {
            const response = await mapView.hitTest(event);
            const stopHit = response.results.find(res => res.graphic?.attributes?.type === "stop");
            
            setTimeout(() => {
                if (stopHit && stopHit.graphic.attributes.tag !== "draft") {
                    const existingStop = stopHit.graphic;
                    addDraftPoint(existingStop.geometry.latitude, existingStop.geometry.longitude, existingStop.attributes.population || 0, true, existingStop.attributes.stopName || "");
                } else {
                    tryAddPoint(event.mapPoint.latitude, event.mapPoint.longitude, 0, false);
                }
            }, 0);
            return;
        }

        // Si NO estamos creando ruta, el clic evalúa si abre la edición directa
        const response = await mapView.hitTest(event);
        const graphicHit = response.results.find(res => 
            res.graphic?.attributes && 
            (res.graphic.attributes.type === "stop" || res.graphic.attributes.type === "line")
        );

        if (graphicHit) {
             const attr = graphicHit.graphic.attributes;
             const screenPoint = mapView.toScreen(graphicHit.mapPoint || event.mapPoint);
             
             if (attr.type === "stop") {
                 const geometry = graphicHit.graphic.geometry;
                 
                 // 🔥 LÓGICA CLAVE: Verificamos si la parada se puede editar
                 const canEdit = !attr.isFromExisting;

                 // Si se puede editar, precargamos los valores en los inputs de React
                 if (canEdit) {
                     setEditValues({
                         name: attr.stopName || "",
                         weight: attr.population || 0
                     });
                 }

                 handleSetCustomPopup({
                     x: screenPoint.x, y: screenPoint.y - 40,
                     title: attr.isShared ? "Parada Compartida" : "Parada",
                     subtitle: attr.stopName || "Sin Nombre",
                     extra: `Peso: ${attr.population || 0}`,
                     isStop: true,
                     isEditing: canEdit, // 🔥 SE ABRE DIRECTO EN MODO EDICIÓN SI ES CANDIDATA
                     lat: geometry.latitude, lon: geometry.longitude,
                     currentName: attr.stopName, currentWeight: attr.population, stopId: attr.stopId,
                     routeColor: attr.routeColor,
                     isFromExisting: attr.isFromExisting,
                     isFixed: true // Lo fijamos para que puedas escribir sin que se cierre
                 });
             } else if (attr.type === "line") {
                 let title = attr.isWinner ? "🏆 RUTA GANADORA" : (attr.routeType === "existing" ? "Ruta Dominada" : "Ruta Candidata");
                 handleSetCustomPopup({ x: screenPoint.x, y: screenPoint.y - 20, title, subtitle: attr.routeName || "Sin nombre", isStop: false, routeColor: attr.routeColor, isFixed: true });
             }
        } else {
             // Si haces clic en el fondo del mapa (vacío), cierra todo
             handleSetCustomPopup(null);
        }
      });

      // 3. LIMPIEZA AL SACAR EL RATÓN DEL MAPA
      mapView.on("pointer-leave", () => {
         // Si está fijo (porque hiciste clic), no se borra. 
         if (!customPopupRef.current?.isFixed) {
             handleSetCustomPopup(null);
             document.body.style.cursor = "default";
         }
      });

    };
    initMap();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { window.isCreatingMode = isCreating; window.currentCity = selectedCity; }, [isCreating, selectedCity]);
  

  return (
    <>
      <TopHeader title="Expansión de Red" />
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
      <div className="content-grid" style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
        
        <SidePanel title="Planificación" isConnected={!!token}>
          <div style={{ marginBottom: 20, position: 'relative' }}>
             <label style={styles.label}>1. Ciudad Objetivo</label>
             <div onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)} style={styles.dropdownHeader(!!selectedCity)}>
               <span style={{fontWeight: 700, color: selectedCity ? '#1F2937' : '#9CA3AF'}}>{selectedCity ? selectedCity.name : "Seleccionar..."}</span>
               <span style={{fontSize: 10, color: '#EAC024'}}>▼</span>
             </div>
             {isCityDropdownOpen && (
               <div style={styles.dropdownList}>{cities.map(c => <div key={c.id} style={styles.dropdownItem} onClick={() => selectCity(c)}>{c.name}</div>)}</div>
             )}
          </div>
          
          {selectedCity && (
            <>
               <div style={{ marginBottom: 15 }}>
                <CollapsibleCard title="Rutas Dominadas" subtitle={`${visibleRouteIds.size} visibles`} isActive={visibleRouteIds.size > 0} onToggleActive={toggleAllExisting} defaultExpanded={true}>
                  {existingRoutes.map(route => (<RouteRow key={route.id} name={route.name} isVisible={visibleRouteIds.has(route.id)} onToggle={() => toggleExistingRoute(route)} />))}
                </CollapsibleCard>
               </div>

               <div style={{ marginBottom: 15 }}>
                <CollapsibleCard title="Candidatas" subtitle={`${candidateRoutes.length} propuestas`} isActive={visibleCandidateIds.size > 0} onToggleActive={toggleAllCandidates} defaultExpanded={true}>
                  {candidateRoutes.length === 0 && <p style={styles.emptyText}>Sin rutas creadas.</p>}
                  {candidateRoutes.map(route => (<RouteRow key={route.id} name={route.name} isVisible={visibleCandidateIds.has(route.id)} onToggle={() => toggleCandidateRoute(route)} onDelete={() => deleteCandidateRoute(route.id)} />))}
                </CollapsibleCard>
               </div>

               <div style={styles.editorContainer}>
                {!isCreating ? (
                  <>
                    <button style={styles.createBtn} onClick={startCreating}>+ Crear Nueva Ruta</button>
                <div style={{marginTop: 10, marginBottom: 10, display: 'flex', gap: '8px'}}>
                <label style={{...styles.excelBtn, flex: 1, margin: 0}}>
                    📂 Cargar Excel (.xlsx)
                    <input type="file" accept=".xlsx, .xls, .csv" style={{display:'none'}} onChange={handleExcelImport} />
                </label>
                <button 
                    onClick={() => setIsExcelModalOpen(true)}
                    style={{
                        backgroundColor: '#ECFDF5', 
                        border: '1px dashed #10B981', 
                        color: '#047857', 
                        borderRadius: '8px', 
                        width: '45px', 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#D1FAE5';
                        e.currentTarget.style.transform = 'scale(1.02)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#ECFDF5';
                        e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title="Ver formato requerido"
                >
                    {/* 🔥 ÍCONO SVG PROFESIONAL 🔥 */}
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                </button>
            </div>
                    {/* 🔥 BOTÓN DE SIMULAR CON CARGA 🔥 */}
                    {candidateRoutes.length > 0 && (
                        <button 
                            style={{
                                ...styles.simBtn,
                                cursor: isSimulating ? 'not-allowed' : 'pointer',
                                opacity: isSimulating ? 0.7 : 1
                            }} 
                            onClick={handleRunSimulation} 
                            disabled={isSimulating}
                        >
                            {isSimulating ? (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 1s linear infinite" }}>
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.3"></circle>
                                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round"></path>
                                    </svg>
                                    Simulando...
                                </>
                            ) : "Simular"}
                        </button>
                    )}
                  </>
                ) : (
                  // 🔥 EDITOR CON ESTADO LOCAL
                  <RouteEditor 
                    draftRoute={draftRoute} 
                    onUpdateStops={handleUpdateStops} 
                    onAddPoint={tryAddPoint} 
                    onSave={saveCandidateRoute} 
                    onCancel={cancelCreating}
                  />
                )}
               </div>
            </>
          )}
        </SidePanel>

        <div style={{flex: 1, position: 'relative', display: 'flex', flexDirection: 'column'}}>
            <MapViewComponent mapRef={mapRef} />
            {customPopup && (
                <div style={styles.tooltipCard(customPopup)}>
                    {customPopup.isEditing ? (
                        <div style={{display:'flex', flexDirection:'column', gap: 8}}>
                            <span style={{fontSize:10, fontWeight:'bold', color:'#D97706'}}>EDITAR PARADA</span>
                            <input type="text" placeholder="Nombre" value={editValues.name} onChange={(e) => setEditValues({...editValues, name: e.target.value})} style={styles.miniInput} />
                            <input type="number" placeholder="Peso" value={editValues.weight} onChange={(e) => setEditValues({...editValues, weight: e.target.value})} style={styles.miniInput} />
                            <div style={{display:'flex', gap: 5, justifyContent:'flex-end'}}>
                                <button onClick={() => handleSetCustomPopup(null)} style={{...styles.tinyBtn, background:'#F3F4F6', color:'#374151'}}>Cancel</button>
                                <button onClick={() => updateGlobalStopData(customPopup.lat, customPopup.lon, editValues.name, editValues.weight)} style={{...styles.tinyBtn, background:'#EAC024', color:'white'}}>Guardar</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                                <div>
                                    <h4 style={{margin:0, fontSize:11, color:'#9CA3AF', textTransform:'uppercase', fontWeight:700}}>{customPopup.title}</h4>
                                    <p style={{margin:'4px 0 0 0', fontSize:14, fontWeight:700, color:'#1F2937'}}>{customPopup.subtitle}</p>
                                    {customPopup.extra && <p style={{margin:'2px 0 0 0', fontSize:11, color:'#6B7280'}}>{customPopup.extra}</p>}
                                </div>
                                {customPopup.isStop && !customPopup.isFromExisting && (
                                    <div onClick={() => { setEditValues({ name: customPopup.currentName || "", weight: customPopup.currentWeight || 0 }); setCustomPopup({...customPopup, isEditing: true}); }} style={styles.editBtn}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {/* 🔥 La flecha del popup ahora usa el color asignado */}
                    <div style={styles.tooltipArrow(customPopup)}></div>
                </div>
            )}
            
            {simulationResult && (
                <div style={styles.floatingWidget}>
                    <div style={styles.widgetHeader}>
                        <span style={{fontSize: 14, fontWeight: 800, color: '#111827', textTransform:'uppercase'}}>Resultados</span>
                        <div onClick={() => setSimulationResult(null)} style={{cursor: 'pointer', fontSize: 16, color:'#9CA3AF', fontWeight:'bold'}}>✕</div>
                    </div>
                    <div style={styles.widgetContent}>
                        <div style={styles.winnerCard}>
                            <div style={{fontSize: 28, marginBottom: 5}}>🏆</div>
                            <h3 style={{margin: 0, color: '#065F46', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1}}>Mejor Opción</h3>
                            <p style={{fontSize: 15, fontWeight: '800', margin: '4px 0', color: '#1F2937'}}>{simulationResult.winningRouteName}</p>
                            <div style={styles.statBadge}>+{simulationResult.peopleGained} personas</div>
                        </div>
                        <div style={styles.statsGrid}>
                            <div style={styles.statBox}><span style={styles.statLabel}>Actual</span><span style={styles.statValue}>{simulationResult.currentCoveragePercent}%</span></div>
                            <div style={styles.statBox}><span style={styles.statLabel}>Nueva</span><span style={{...styles.statValue, color: '#10B981'}}>{simulationResult.newCoveragePercent}%</span></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
      </div>
      {/* 🔥 MODAL DE AYUDA DE EXCEL 🔥 */}
      {isExcelModalOpen && (
        <div style={{ 
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, 
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            backdropFilter: 'blur(3px)' 
        }}>
            <div style={{ 
                background: 'white', borderRadius: '16px', padding: '24px', 
                width: '90%', maxWidth: '520px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', 
                position: 'relative', animation: 'fadeIn 0.2s ease-out' 
            }}>
                <button 
                    onClick={() => setIsExcelModalOpen(false)} 
                    style={{ position: 'absolute', top: 16, right: 16, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px', color: '#9CA3AF' }}
                >✕</button>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ background: '#ECFDF5', padding: '8px', borderRadius: '8px', color: '#10B981' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                    </div>
                    <h3 style={{ margin: 0, color: '#1F2937', fontSize: '18px', fontWeight: 800 }}>
                        Formato de Importación
                    </h3>
                </div>
                
                <p style={{ fontSize: '13px', color: '#4B5563', marginBottom: '16px', lineHeight: 1.5 }}>
                    Para cargar rutas masivamente, tu archivo debe tener las siguientes <strong>5 columnas exactas</strong> en la primera fila. 
                    <br/><br/>
                    💡 <strong>Observación:</strong> Debes <strong>combinar las celdas</strong> de la columna "Nombre Ruta" para agrupar todas las paradas que pertenecen a un mismo recorrido, tal como se ve en el ejemplo de abajo.
                </p>

                {/* Tabla visual de ejemplo con "rowSpan" para simular celdas combinadas */}
                <div style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '12px', marginBottom: '20px', overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '11px', textAlign: 'left', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #D1D5DB' }}>
                                <th style={{ padding: '8px 6px', color: '#374151', borderRight: '1px solid #E5E7EB' }}>Nombre Ruta</th>
                                <th style={{ padding: '8px 6px', color: '#374151' }}>Nombre Parada</th>
                                <th style={{ padding: '8px 6px', color: '#374151' }}>Peso</th>
                                <th style={{ padding: '8px 6px', color: '#374151' }}>Latitud</th>
                                <th style={{ padding: '8px 6px', color: '#374151' }}>Longitud</th>
                            </tr>
                        </thead>
                <tbody>
                            {/* --- PRIMERA RUTA --- */}
                            <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                                <td rowSpan="3" style={{ padding: '8px 6px', fontWeight: 'bold', color: '#047857', borderRight: '1px solid #E5E7EB', verticalAlign: 'middle', backgroundColor: '#ECFDF5' }}>
                                    Ruta Porlamar - Pampatar
                                </td>
                                <td style={{ padding: '8px 6px' }}>Terminal de Porlamar</td>
                                <td style={{ padding: '8px 6px', color: '#D97706', fontWeight: 'bold' }}>1000</td>
                                <td style={{ padding: '8px 6px' }}>10.953</td>
                                <td style={{ padding: '8px 6px' }}>-63.851</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                                <td style={{ padding: '8px 6px' }}>C.C. Sambil Margarita</td>
                                <td style={{ padding: '8px 6px', color: '#D97706', fontWeight: 'bold' }}>50</td>
                                <td style={{ padding: '8px 6px' }}>10.985</td>
                                <td style={{ padding: '8px 6px' }}>-63.815</td>
                            </tr>
                            <tr style={{ borderBottom: '2px solid #D1D5DB' }}>
                                <td style={{ padding: '8px 6px' }}>Castillo San Carlos</td>
                                <td style={{ padding: '8px 6px', color: '#D97706', fontWeight: 'bold' }}>50</td>
                                <td style={{ padding: '8px 6px' }}>10.996</td>
                                <td style={{ padding: '8px 6px' }}>-63.798</td>
                            </tr>

                            {/* --- SEGUNDA RUTA --- */}
                            <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                                <td rowSpan="3" style={{ padding: '8px 6px', fontWeight: 'bold', color: '#047857', borderRight: '1px solid #E5E7EB', verticalAlign: 'middle', backgroundColor: '#ECFDF5' }}>
                                    Ruta Sambil - La Asunción
                                </td>
                                <td style={{ padding: '8px 6px' }}>C.C. Sambil Margarita</td>
                                <td style={{ padding: '8px 6px', color: '#D97706', fontWeight: 'bold' }}>50</td>
                                <td style={{ padding: '8px 6px' }}>10.985</td>
                                <td style={{ padding: '8px 6px' }}>-63.815</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                                <td style={{ padding: '8px 6px' }}>Plaza Bolívar La Asunción</td>
                                <td style={{ padding: '8px 6px', color: '#D97706', fontWeight: 'bold' }}>40</td>
                                <td style={{ padding: '8px 6px' }}>11.032</td>
                                <td style={{ padding: '8px 6px' }}>-63.862</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <button 
                    onClick={downloadExcelTemplate}
                    style={{ 
                        width: '100%', padding: '12px', borderRadius: '8px', border: 'none', 
                        backgroundColor: '#10B981', color: 'white', fontWeight: 'bold', 
                        cursor: 'pointer', display: 'flex', justifyContent: 'center', 
                        alignItems: 'center', gap: '8px', transition: 'background 0.2s',
                        boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#10B981'}
                >
                    Descargar Plantilla de Ejemplo
                </button>
            </div>
        </div>
      )}
      <Modal isOpen={modal.isOpen} title={modal.title} message={modal.message} type={modal.type} onClose={closeModal} />
    </>
  );
}

const styles = {
  label: { fontSize: 11, fontWeight: 'bold', color: '#6B7280', textTransform: 'uppercase', marginBottom: 6, display: 'block' },
  dropdownHeader: (active) => ({ width: '100%', padding: '12px 16px', borderRadius: 12, backgroundColor: active ? '#FFFBEB' : 'white', border: active ? '2px solid #EAC024' : '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }),
  dropdownList: { position: 'absolute', top: '110%', left: 0, width: '100%', backgroundColor: 'white', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', zIndex: 50, overflow: 'hidden', border:'1px solid #E5E7EB' },
  dropdownItem: { padding: '10px 15px', fontSize: 14, cursor: 'pointer', borderBottom: '1px solid #F3F4F6', color:'#374151' },
  emptyText: { fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', padding: '10px 0' },
  editorContainer: { marginTop: 20 },
  createBtn: { width: '100%', padding: 12, borderRadius: 8, border: 'none', backgroundColor: '#EAC024', color: 'white', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(234, 192, 36, 0.2)' },
  excelBtn: { width: '100%', padding: 12, borderRadius: 8, border: '1px dashed #10B981', backgroundColor: '#ECFDF5', color: '#047857', fontWeight: 'bold', cursor: 'pointer', display:'flex', justifyContent:'center', alignItems:'center', fontSize: '13px' },
  simBtn: { width: '100%', marginTop: 15, padding: 14, borderRadius: 10, border: 'none', backgroundColor: '#1F2937', color: 'white', fontWeight: '800', cursor: 'pointer', fontSize: '13px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, boxShadow: '0 4px 10px rgba(0,0,0,0.2)' },
  floatingWidget: { position: 'absolute', top: '20px', right: '20px', width: '260px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', zIndex: 40, display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'fadeIn 0.3s ease-out' },
  widgetHeader: { padding: '12px 16px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#FAFAFA', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  widgetContent: { padding: '16px' },
  winnerCard: { backgroundColor: '#ECFDF5', border: '1px solid #10B981', borderRadius: '10px', padding: '12px', textAlign: 'center', marginBottom: '15px' },
  statBadge: { display: 'inline-block', backgroundColor: '#10B981', color: 'white', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', marginTop: 5 },
  statsGrid: { display: 'flex', gap: 8, marginBottom: 0 },
  statBox: { flex: 1, backgroundColor: '#F9FAFB', padding: '6px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  statLabel: { fontSize: 9, color: '#6B7280', textTransform: 'uppercase', fontWeight: 'bold' },
  statValue: { fontSize: 13, fontWeight: '800', color: '#374151' },
  // 🔥 Los popups flotantes ahora detectan el color inyectado desde los puntos del mapa
  tooltipCard: (customPopup) => ({ position: 'absolute', left: customPopup.x, top: customPopup.y, transform: 'translate(-50%, -100%)', backgroundColor: 'white', padding: '10px 14px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.15)', zIndex: 50, border: `2px solid ${customPopup.routeColor || (customPopup.isStop ? '#EF4444' : '#EAC024')}`, marginBottom: '10px', animation: 'fadeIn 0.2s ease-out', minWidth: '140px', pointerEvents: 'auto' }),
  tooltipArrow: (customPopup) => ({ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%) rotate(45deg)', width: 10, height: 10, backgroundColor: 'white', borderRight: `2px solid ${customPopup?.routeColor || (customPopup?.isStop ? '#EF4444' : '#EAC024')}`, borderBottom: `2px solid ${customPopup?.routeColor || (customPopup?.isStop ? '#EF4444' : '#EAC024')}` }),
  miniInput: { border:'1px solid #D1D5DB', borderRadius:4, padding:'4px', fontSize:11, width: '100%' },
  tinyBtn: { border:'none', borderRadius:4, padding:'4px 8px', fontSize:10, fontWeight:'bold', cursor:'pointer' },
  editBtn: { width: 24, height: 24, borderRadius: '6px', backgroundColor: '#F3F4F6', border: '1px solid #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginLeft: 8 }
};